import React from 'react'
import { Element, Box, Button } from 'react-bulma-components'
import { Field, FieldArray, useFormikContext } from 'formik'
import FormField from '../../../form/fields/form-field'
import CheckboxField from '../../../form/fields/checkbox'
import { WorkersRequestPostsSuggestions } from '../../../../hooks/use-workers-request-posts-suggestions'
import { WorkersRequest } from '../../../../api/workers-requests'
import { checkAvailibilityInPeriod } from '../../../../utils/users'
import { compareDocsById } from '../../../../utils/documents'
import WorkerInputLabel from '../worker-input-label'
import { SuggestedWorker } from './multiple-assigns-form'

interface SelectPostsListProps {
  suggestions: Omit<WorkersRequestPostsSuggestions, 'workers'> & { workers: SuggestedWorker[] }
  workersRequest: WorkersRequest
  currentJobTile: string
  submitButton?: {
    label: string
    loading: boolean
    disabled: boolean
  }
}

interface FormValues {
  selectedWorkerIds: string[]
  currentJobTile: string
}

const SelectButton: React.FC<{
  onClick: () => void
  label: string
  dataTest?: string
}> = ({ onClick, label, dataTest }) => {
  return (
    <Button
      size="small"
      color="secondary"
      onClick={onClick}
      mr={2}
      type="button"
      data-test={dataTest}
    >
      {label}
    </Button>
  )
}

const SelectPostsList: React.FC<SelectPostsListProps> = ({
  suggestions,
  workersRequest,
  currentJobTile,
  submitButton,
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>()

  const currentWorkersData = suggestions.workers
  const selectedPostsCount = values.selectedWorkerIds.filter(id => id).length

  // Gérer la sélection de tous les travailleurs
  const handleSelectAll = () => {
    const workersCount = currentWorkersData?.length || 0
    const postsCount = suggestions.availablePosts.length
    const selectedWorkerIds = []

    for (let i = 0; i < workersCount; i++) {
      const worker = currentWorkersData[i]
      if (selectedWorkerIds.filter(Boolean).length >= postsCount) break

      if (worker) {
        const workerRelationWithEmployer = worker.employerRelations.find((relation: any) =>
          compareDocsById(relation.employer, workersRequest.employer),
        )
        const isAvailable = checkAvailibilityInPeriod(
          new Date(workersRequest.missionsStart),
          new Date(workersRequest.missionsEnd),
          worker.availability,
        )

        if (workerRelationWithEmployer?.collaboration?.isInactive || !isAvailable) {
          selectedWorkerIds.push(undefined)
        } else {
          selectedWorkerIds.push(worker._id)
        }
      }
    }

    setFieldValue('selectedWorkerIds', selectedWorkerIds)
  }

  // Gérer la désélection de tous les travailleurs
  const handleDeselectAll = () => {
    setFieldValue('selectedWorkerIds', currentWorkersData?.map(() => undefined) || [])
  }

  React.useEffect(() => {
    handleDeselectAll()
  }, [currentJobTile])

  if (!currentWorkersData) return null

  const isMaxSelected = selectedPostsCount === suggestions.availablePosts.length

  const selectAllCount =
    suggestions.workers.length < suggestions.availablePosts.length
      ? suggestions.workers.length
      : suggestions.availablePosts.length

  return (
    <FormField>
      {/* Boutons de sélection globale */}
      <Element mb={2}>
        {currentWorkersData.length > 0 && (
          <Element display="flex" justifyContent="space-between" alignItems="center">
            <Element textSize={7} textColor={'grey'}>
              {`${currentWorkersData?.length} candidats disponibles pour ${suggestions.availablePosts.length} poste(s)`}
              <br />
              <Element
                renderAs="span"
                textColor={selectedPostsCount === 0 ? 'danger' : 'grey'}
              >{`${selectedPostsCount} sélectionné(s)`}</Element>
            </Element>
            <Element display="flex">
              {submitButton && (
                <Button
                  type="submit"
                  color="primary"
                  loading={submitButton.loading}
                  disabled={submitButton.disabled}
                  fullwidth
                  data-test="auto-assign-posts-form-button"
                  size="small"
                  mr={2}
                >
                  {submitButton.label}
                </Button>
              )}
              <SelectButton
                onClick={handleSelectAll}
                label={`Sélectionner ${selectAllCount} poste(s)`}
                dataTest="select-all-posts"
              />
              <SelectButton
                onClick={handleDeselectAll}
                label="Désélectionner"
                dataTest="deselect-all-posts"
              />
            </Element>
          </Element>
        )}
      </Element>

      {/* Liste des checkboxes avec FieldArray */}
      <FieldArray name="selectedWorkerIds">
        {() => (
          <>
            {!currentWorkersData.length && (
              <Element textSize={6} textAlign="center" mt={6} mb={4} textColor="grey">
                Aucun intérimaire à afficher
              </Element>
            )}
            {currentWorkersData.map((worker: any, index: number) => (
              <Box key={worker._id} py={3}>
                <Field
                  component={CheckboxField}
                  name={`selectedWorkerIds.${index}`}
                  type="checkbox"
                  disabled={
                    (isMaxSelected && !values.selectedWorkerIds.includes(worker._id)) ||
                    worker.isInactiveWithEmployer
                  }
                  text={
                    <Element renderAs="span">
                      <WorkerInputLabel
                        user={worker}
                        post={suggestions.availablePosts[index]!}
                        workersRequest={workersRequest}
                        isDesiredWorker={
                          currentJobTile && worker.desiredJobTitleIds.includes(currentJobTile)
                        }
                        lastMissionStartDate={worker.lastMissionStartDate}
                      />
                    </Element>
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newSelectedWorkerIds = [...values.selectedWorkerIds]
                    if (e.target.checked) {
                      newSelectedWorkerIds[index] = worker._id
                      setFieldValue('selectedWorkerIds', newSelectedWorkerIds)
                    } else {
                      newSelectedWorkerIds[index] = ''
                      setFieldValue('selectedWorkerIds', newSelectedWorkerIds)
                    }
                  }}
                />
              </Box>
            ))}
          </>
        )}
      </FieldArray>
    </FormField>
  )
}

export default SelectPostsList
